/* eslint-disable no-console */
import { Lightning, Router } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { fetchLayout } from '../../logic/layout/layout.actions';
import { getLayoutEntityAsString } from '../../logic/layout/layout.utils';
import { setRouteSkeletonByEntity } from '../../logic/routing/routing.actions';
import { getContentPadding } from '../layout/abstractLayout.style';
import { getSkeletonByConfig } from '../tornado/skeleton/skelton.provider';
import { pageTransition } from './platform/default/pageTransition';
import connect from './page.connector';
import { defaultContext, RENDERER_HEIGHT } from './page.style';
import { getRenderer } from './page.utils';

export class Page extends Lightning.Component {
  static _template() {
    return {
      Renderer: {},
      Skeleton: {},
    };
  }

  _onUrlParams(params) {
    const { entityType, entityId, [Router.symbols.historyState]: historyState } = params;
    // NOTE: Ensure history state is provided as early as possible so when a layout is set from a backtrack, it will be shifted to a correct position
    this._history = historyState;
    // NOTE: it will help to ensure that a new layout will not be applied to a previous page on backtrack
    Page.currentTarget = this.id;
    Store.dispatch(fetchLayout(entityType, entityId, historyState?.query));
    Store.dispatch(setRouteSkeletonByEntity(entityType, entityId));
  }

  _construct() {
    this._style = defaultContext;
    this._entity = '';
    this._reuseRenderer = true;
  }

  _build() {
    this._skeletonFadeAnimation = this.tag('Skeleton').animation({
      duration: 0.5,
      actions: [{ p: 'alpha', v: { 0: 1, 1: 0 } }],
      stopMethod: 'immediate',
    });
  }

  _active() {
    this._unsubscribe = connect(this);
    this._skeletonFadeAnimation.on('finish', () => {
      if (!this._skeleton) {
        this.tag('Skeleton').children = [];
      }
    });
  }

  _inactive() {
    this._cleanup();
  }

  historyState(history) {
    if (history) {
      this._history = history;
    }

    const renderer = this.tag('Renderer').children[0];
    if (renderer && typeof renderer.historyState === 'function') {
      return renderer.historyState(history);
    }

    return undefined;
  }

  set layout(value) {
    if (!value) {
      return;
    }

    // An ugly hack to avoid Lightning bug when a previous page is detached after
    // a new one is attached (and therefore the previous page may render the same layout as a new one)
    clearTimeout(this._layoutTimeout);
    this._layoutTimeout = setTimeout(() => {
      if (!Page.currentTarget || Page.currentTarget === this.id) {
        this._applyLayout(value);
      }
    }, 0);
  }

  set skeleton(value) {
    if (value && this._skeleton !== value) {
      const skeleton = getSkeletonByConfig(value);
      this._skeletonFadeAnimation.stop();
      if (skeleton) {
        this.tag('Skeleton').patch({
          alpha: 1,
          children: [skeleton],
        });
        // NOTE: a side navigation state is applied before a skeleton appears, so the padding is re-applied
        // to let a just created placeholder opt out of it
        this._applyContentPadding();
      }
    } else if (!value) {
      if (this._skeletonFadeAnimation) {
        this._skeletonFadeAnimation.start();
      }
    }
    this._skeleton = value;
  }

  set hasSideNav(value) {
    if (this._hasSideNav !== value) {
      this._hasSideNav = value;
      this._applyContentPadding();
    }
  }

  pageTransition(pageIn, pageOut) {
    return pageTransition(pageIn, pageOut);
  }

  _getFocused() {
    return this.tag('Renderer').children[0];
  }

  _applyLayout(value) {
    const entity = getLayoutEntityAsString(value);
    const isDifferentLayout = this._entity !== entity;
    this._entity = entity;

    const type = getRenderer(value);
    const [currentRenderer] = this.tag('Renderer').children;

    if ((this._reuseRenderer || !isDifferentLayout) && currentRenderer instanceof type) {
      currentRenderer.data = value;
    } else {
      this.tag('Renderer').children = [
        {
          type,
          h: RENDERER_HEIGHT[this._style.screenSize],
        },
      ];
      const renderer = this.tag('Renderer').children[0];
      this._restoreHistory(renderer);
      renderer.data = value;
      this._refocus();
    }
  }

  _applyContentPadding() {
    const root = this.tag('Skeleton');

    if (root.children?.length) {
      const [skeleton] = root.children;
      const value = skeleton && skeleton.ignoresContentPadding ? 0 : getContentPadding(this._hasSideNav);
      root.x = value;
    }
  }

  _restoreHistory(renderer) {
    if (this._history && typeof renderer.historyState === 'function') {
      renderer.historyState(this._history);
      this._history = undefined;
    }
  }

  _cleanup() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    clearTimeout(this._layoutTimeout);
    if (this._skeletonFadeAnimation) {
      this._skeletonFadeAnimation.removeAllListeners('finish');
    }
    this._skeleton = null;
    this._skeletonFadeAnimation = null;
  }
}
